<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { useCart } from '@/composables/useCart';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { formatCents } = useCurrencyFormatter();

const props = defineProps({
    siteId: {
        type: Number,
        required: true
    },
    price: {
        type: Number,
        required: true
    },
    coin: {
        type: String,
        default: 'USD'
    }
});

const { addToCart, updateQuantity: updateQty, getItemQuantity, isInCart, loadCart, cartItems } = useCart();

// Local quantity state
const quantity = ref(0);
const loading = ref(false);

// Check if item is in cart and get initial quantity
const checkCartStatus = () => {
    quantity.value = getItemQuantity(props.siteId);
};

onMounted(() => {

  checkCartStatus();

  const handleCartUpdate = () => {
    checkCartStatus();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('cart-item-added', handleCartUpdate);
    window.addEventListener('cart-updated', handleCartUpdate);
  }

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('cart-item-added', handleCartUpdate);
      window.removeEventListener('cart-updated', handleCartUpdate);
    }
  });
});

watch(
  () => getItemQuantity(props.siteId),
  (newQuantity) => { quantity.value = newQuantity; },
  { immediate: true }
);

watch(cartItems, () => {
  checkCartStatus();
}, { deep: true });

// Add to cart (initial click)
const handleAddToCart = async () => {
    loading.value = true;
    try {
        await addToCart(props.siteId, 1);
        quantity.value = 1;

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Added to cart'),
            life: 2000
        });
    } catch (error) {
        console.error('Error adding to cart:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to add to cart'),
            life: 3000
        });
    } finally {
        loading.value = false;
    }
};

// Increment quantity
const increment = async () => {
    if (quantity.value >= 50) {
        toast.add({
            severity: 'warn',
            summary: $t('Limit Reached'),
            detail: $t('Maximum 50 links per site'),
            life: 3000
        });
        return;
    }

    loading.value = true;
    try {
        const newQuantity = quantity.value + 1;
        await updateQty(props.siteId, newQuantity);
        quantity.value = newQuantity;
        
        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('One more item added to cart'),
            life: 3000
        });
    } catch (error) {
        console.error('Error updating quantity:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to update quantity'),
            life: 3000
        });
    } finally {
        loading.value = false;
    }
};

// Decrement quantity
const decrement = async () => {
    loading.value = true;
    try {
        if (quantity.value === 1) {
            await updateQty(props.siteId, 0);
            quantity.value = 0;
            
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Item removed from cart'),
                life: 3000
            });
        } else {
            const newQuantity = quantity.value - 1;
            await updateQty(props.siteId, newQuantity);
            quantity.value = newQuantity;

            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('One more item removed from cart'),
                life: 3000
            });
        }
    } catch (error) {
        console.error('Error updating quantity:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to update quantity'),
            life: 3000
        });
    } finally {
        loading.value = false;
    }
};

// Format price
const formatPrice = computed(() => {
    return formatCents(props.price, props.coin);
});

// Show button or counter
const showCounter = computed(() => quantity.value > 0);
</script>

<template>
  <div class="site-cart-button">
    <Button
      v-if="!showCounter"
      :label="`${$t('Buy')}`"
      icon="pi pi-shopping-cart"
      size="small"
      @click="handleAddToCart"
      :loading="loading"
      :disabled="loading"
    />

        <div v-else class="flex border rounded-[6px] overflow-hidden w-full max-w-[120px] items-center">
            <Button
                icon="pi pi-minus"
                severity="secondary"
                text
                size="small"
                @click="decrement"
                :disabled="loading"
            />
            <span class="w-6 text-center font-semibold text-sm flex items-center justify-center">{{ quantity }}</span>
            <Button
                icon="pi pi-plus"
                severity="secondary"
                text
                size="small"
                @click="increment"
                :disabled="loading || quantity >= 50"
            />
        </div>
  </div>
</template>

<style scoped>

.flex.border .p-button :deep(.p-button-icon) {
  font-size: 0.65rem !important;
}

.p-button-sm.p-button-icon-only{
    width: 1.3rem;
}
.site-cart-button {
    display: inline-flex;
    align-items: center;
}
</style>
